import { EMPTY_FN, isEmpty, openInNewTab, redirect } from '@libs/shared';
import {
    ChooseImageOptions,
    ShareLinkOptions,
    ShareLinkResult,
    DingConfig,
    LocationResult,
    ScanType,
    SdkError,
    SDKFunction,
    SdkType,
    type SetHeaderActionsParams,
    type ClearHeaderActionsParams,
    type HeaderActionClickEvent,
    type AddToDesktopParams,
    type AddToDesktopResult
} from '../interface';
import { defaultImpl, sdkLog } from '../decorator';
import { sdkLogger } from '../utils';
import { SdkResponseCode } from '../constant';

/**
 * jssdk基类，有以下两个作用：
 * 1、提供JS SDK API 列表参考
 * 2、提供默认实现
 */
export default class JsSdkBase {
    sdkType: SdkType = SdkType.Browser;
    /**
     * sdk 是否鉴权通过
     * 注意，该鉴权结果是由 xxx.config 决定的，并在 xxx.ready 中获取结果
     */
    isSdkReady = false;
    /**
     * sdk 脚本是否加载完成
     */
    isSdkLoaded = false;
    /**
     * 初始化过程失败时的错误码
     */
    initFailErrCode?: Symbol;
    /**
     * init 过程是否结束
     */
    isSdkInitCompleted = false;
    /**
     * 异步获取jssdk鉴权参数
     */
    fetchConfig: (url?: string) => Promise<Pick<JsSdkBase, 'jsSdkConfig' | 'wxAgentJsSdkConfig'>> =
        () => {
            return Promise.resolve({
                jsSdkConfig: {},
                wxAgentJsSdkConfig: {}
            });
        };
    /**
     * jssdk 鉴权参数
     */
    jsSdkConfig: {
        appId?: string;
        timestamp?: number;
        nonceStr?: string;
        signature?: string;
        jsApiList?: string[];
        clientId?: string;
    } = {};
    /**
     * 企微jssdk所额外需要的鉴权参数
     * @see https://developer.work.weixin.qq.com/document/path/94313#%E9%80%9A%E8%BF%87agentconfig%E6%B3%A8%E5%85%A5%E5%BA%94%E7%94%A8%E7%9A%84%E6%9D%83%E9%99%90
     */
    wxAgentJsSdkConfig: {
        corpid?: string;
        agentid?: string;
        timestamp?: string;
        nonceStr?: string;
        signature?: string;
        jsApiList?: string[];
    } = {};
    /**
     * jssdk 脚本加载缓存
     */
    loadScriptCache?: Promise<void | [any, any]>;
    /**
     * 存储业务侧提供的默认实现
     */
    defaultImplementations: Map<string, (args: any) => void | Promise<any>> = new Map();
    /**
     * ready 回调执行队列
     */
    readyCallbacks: Function[] = [];
    /**
     * 脚本加载完成回调执行队列
     */
    loadedCallbacks: Function[] = [];
    /**
     * init 过程失败后回调执行队列
     */
    errorCallbacks: Function[] = [];
    /**
     * init 流程结束后的的回调执行队列
     */
    initCompletedCallbacks: Function[] = [];

    setIsSdkReady(isReady: boolean) {
        this.isSdkReady = isReady;
        if (isReady) {
            for (const cb of this.readyCallbacks) {
                try {
                    cb();
                } catch (err) {
                    sdkLogger.error('error occurred in h5JsApi.ready() callback', err);
                }
            }
            // 清空队列，防止内存泄漏和重复执行
            this.readyCallbacks = [];
        }
    }

    setIsSdkLoaded(isLoaded: boolean) {
        this.isSdkLoaded = isLoaded;
        if (isLoaded) {
            sdkLogger.info(`${this.sdkType} script loaded`);
            for (const cb of this.loadedCallbacks) {
                try {
                    cb();
                } catch (err) {
                    sdkLogger.error('error occurred in h5JsApi.loaded() callback', err);
                }
            }
            // 清空队列，防止内存泄漏和重复执行
            this.loadedCallbacks = [];
        }
    }

    setInitFailErrCode(errCode: Symbol) {
        this.initFailErrCode = errCode;
        if (errCode) {
            for (const cb of this.errorCallbacks) {
                try {
                    cb(errCode);
                } catch (err) {
                    sdkLogger.error('error occurred in h5JsApi.error() callback', err);
                }
            }
            // 清空队列，防止内存泄漏和重复执行
            this.errorCallbacks = [];
        }
    }

    setIsSdkInitCompleted(isCompleted: boolean) {
        this.isSdkInitCompleted = isCompleted;
        if (isCompleted) {
            for (const cb of this.initCompletedCallbacks) {
                try {
                    cb();
                } catch (err) {
                    sdkLogger.error('error occurred in h5JsApi.completed() callback', err);
                }
            }
            // 清空队列，防止内存泄漏和重复执行
            this.initCompletedCallbacks = [];
        }
    }

    /**
     * 初始化sdk
     * 包括异步加载jssdk脚本、异步加载鉴权配置、jssdk 鉴权
     * @param config 初始化参数
     * @param config.fetchConfig 异步获取鉴权参数
     */
    @sdkLog(SdkType.Browser)
    init(config: { fetchConfig: JsSdkBase['fetchConfig'] }): Promise<void> {
        /**
         * 浏览器环境下，提前终止初始化过程，避免不必要的接口请求
         * 注意，不要滥用 sdkType 判断
         * @see https://fanruan-x.feishu.cn/wiki/FE5xwC1FfilwIpkHV7Wc0XdmnDb?fromScene=spaceOverview#share-JH8TdttnQofBqExkx8qc6N25nFA 方案二
         */
        if (this.sdkType === SdkType.Browser) {
            // 浏览器环境下不需要加载脚本和jssdk鉴权，此处特意认为它已成功，防止业务侧使用loaded或ready回调时出现问题
            this.setIsSdkLoaded(true);
            return Promise.resolve();
        }
        this.fetchConfig = config.fetchConfig;
        return this.asyncLoadScript().then(() => this.loadSdkConfig());
    }

    /**
     * 当sdk初始化完成（完成脚本加载、配置获取、鉴权）后执行该回调
     * 向业务侧暴露
     * @param callback 待执行回调
     */
    ready(callback: () => void) {
        if (this.isSdkReady) {
            callback();
        } else {
            this.readyCallbacks.push(callback);
        }
    }

    /**
     * 当sdk初始化失败时执行该回调
     * 脚本加载、配置获取、鉴权，其中任何一步失败都会触发
     * 向业务侧暴露
     * @param callback 待执行回调
     */
    error(callback: (errCode: Symbol) => void) {
        if (this.initFailErrCode) {
            callback(this.initFailErrCode);
        } else {
            this.errorCallbacks.push(callback);
        }
    }

    /**
     * 当初始化流程结束后，执行该回调
     * @param callback 待执行回调
     */
    completed(callback: () => void) {
        if (this.isSdkInitCompleted) {
            callback();
        } else {
            this.initCompletedCallbacks.push(callback);
        }
    }

    /**
     * 当sdk脚本加载完成后执行该回调
     * 向业务侧暴露
     * @param callback 待执行回调
     */
    loaded(callback: () => void) {
        if (this.isSdkLoaded) {
            callback();
        } else {
            this.loadedCallbacks.push(callback);
        }
    }

    @sdkLog(SdkType.Browser)
    loadScript(): Promise<void | [any, any]> {
        // 浏览器环境下，不用加载脚本
        return Promise.resolve();
    }

    @sdkLog(SdkType.Browser)
    asyncLoadScript() {
        // 如果已经有缓存的Promise，则直接返回
        if (this.loadScriptCache) {
            return this.loadScriptCache;
        }

        // 创建一个新的Promise并缓存
        this.loadScriptCache = this.loadScript()
            .then(() => {
                this.setIsSdkLoaded(true);
            })
            .catch((err) => {
                this.setInitFailErrCode(SdkResponseCode.Init.LoadScriptError);
                // 如果加载失败，清除缓存以便下次重试
                this.loadScriptCache = undefined;
                return Promise.reject(err);
            });

        return this.loadScriptCache;
    }

    /**
     * 异步获取sdk鉴权参数，并进行sdk鉴权操作
     */
    @sdkLog(SdkType.Browser)
    loadSdkConfig() {
        if (!this.fetchConfig) {
            this.setInitFailErrCode(SdkResponseCode.Init.FetchConfigError);
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'fetchConfig is missing')
            );
        }
        return this.fetchConfig().then(
            ({ jsSdkConfig, wxAgentJsSdkConfig }) => {
                this.jsSdkConfig = jsSdkConfig;
                this.wxAgentJsSdkConfig = wxAgentJsSdkConfig;
                if (isEmpty(jsSdkConfig)) {
                    this.setInitFailErrCode(SdkResponseCode.Init.FetchConfigError);
                    return Promise.reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.H5JsApiError,
                            'jsSdkConfig is missing'
                        )
                    );
                }
                sdkLogger.info(`load ${this.sdkType} sdk`, { jsSdkConfig });
                if (!isEmpty(wxAgentJsSdkConfig)) {
                    sdkLogger.info(`load ${this.sdkType} agent sdk`, { wxAgentJsSdkConfig });
                }
                return this.sdkConfig();
            },
            (err) => {
                // 注意，这里不要直接在 fetchConfig().then 后接 catch，防止捕获到其他错误，这里只想捕获 fetchConfig 本身失败时的错误
                this.setInitFailErrCode(SdkResponseCode.Init.FetchConfigError);
                return Promise.reject(err);
            }
        );
    }

    /**
     * jssdk 统一鉴权逻辑
     */
    @sdkLog(SdkType.Browser)
    sdkConfig() {
        return new Promise<void>((resolve, reject) => {
            this._error((err) => {
                this.setInitFailErrCode(SdkResponseCode.Init.SdkConfigError);
                reject(err);
            });
            if (!isEmpty(this.jsSdkConfig)) {
                this._config(this.jsSdkConfig, this.wxAgentJsSdkConfig);
            }
            this._ready(() => {
                this.setIsSdkReady(true);
                resolve();
            });
        });
    }

    /**
     * jssdk鉴权
     * @param _config sdk鉴权配置
     */
    @sdkLog(SdkType.Browser)
    _config(_config?: JsSdkBase['jsSdkConfig'], _agentConfig?: JsSdkBase['wxAgentJsSdkConfig']) {}

    /**
     * 鉴权 & 初始化失败后调用
     * @param callback 验证成功后的回调
     */
    @sdkLog(SdkType.Browser)
    _ready(callback: () => void) {
        callback();
    }

    /**
     * 鉴权 & 初始化成功后调用
     * @param callback 验证失败后的回调
     */
    @sdkLog(SdkType.Browser)
    _error(callback: (err?: any) => void) {
        callback();
    }

    /**
     * 钉钉免费个人版联系管理员按钮的sdk
     *
     * @param _id isv_app_id
     * @param _corpId corpId 配置
     */
    @sdkLog(SdkType.Browser)
    contactAdmin(_id: string, _corpId: string): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 调用当前客户端的分享功能。
     * resolve 返回分享结果：success 表示分享成功，cancelled 表示明确取消
     * reject 表示 SDK 调用失败
     * 部分客户端操作不会触发回调，Promise 会保持 pending。
     */
    @sdkLog(SdkType.Browser)
    shareLink(_options: ShareLinkOptions): Promise<ShareLinkResult | void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 钉钉授权认证
     */
    @sdkLog(SdkType.Browser)
    authConfirm(): Promise<{ code?: any; error?: string }> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 获取钉钉授权码
     * @param _integrateCorpId 企业id
     */
    @sdkLog(SdkType.Browser)
    requestAuthCode(_integrateCorpId: string): Promise<string> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 图片预览
     *
     * @param _urls 图片链接
     * @param _current 当前图片
     */
    @sdkLog(SdkType.Browser)
    previewImage(_urls: string[], _current: string): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 获取地理位置坐标
     *
     * @param _options 配置
     */
    @defaultImpl
    @sdkLog(SdkType.Browser)
    getLocation(_options: { accuracy: number }): Promise<LocationResult> {
        /**
         * 普通浏览器网页环境下，直接使用高德地图定位功能
         * 自动定位失败时，默认使用高德地图定位兜底
         * SDK 定位失败时，开启高德地图的IP定位
         * @see FXD-67861
         * @see https://project.feishu.cn/b2rl2h/issue/detail/6247009115
         */
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 扫码
     *
     * @param options 配置
     * @param options.type 扫码类型
     * @param options.enable 是否启用扫码
     * @param options.html5QrCodeScanner 调用网页扫码逻辑
     * @param options.describe 描述
     */
    @defaultImpl
    @sdkLog(SdkType.Browser)
    scanCode(options: { type: ScanType; enable: boolean }): Promise<any> {
        const { enable } = options;
        if (!enable) {
            return Promise.reject(
                new SdkError(this.sdkType, SdkResponseCode.H5JsApiError, 'scanCode not enable')
            );
        }
        // 不支持集成模式扫码的时候，采用网页扫码策略
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 拍照或从手机相册中选图接口
     * @param _options 配置
     */
    @sdkLog(SdkType.Browser)
    chooseImage(_options: ChooseImageOptions): Promise<File[]> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 上传图片
     * @param _localId 图片ID
     */
    @sdkLog(SdkType.Browser)
    uploadImage(_localId: string): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 发送消息
     *
     * @param _options 配置
     */
    @sdkLog(SdkType.Browser)
    notify(_options: DingConfig): Promise<{ success: boolean }> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * confirm弹窗，钉钉环境使用其特有confirm
     * @param config 配置项
     * @param config.message 弹窗文案
     * @param config.title 标题
     * @param config.cancelButtonText 取消按钮文字
     * @param config.confirmButtonText 确认按钮文字
     * @see FXD-55239
     */
    @sdkLog(SdkType.Browser)
    confirm(config: {
        message: string;
        title?: string;
        cancelButtonText?: string;
        confirmButtonText?: string;
    }) {
        const allowTransition = window.confirm(config.message);
        return Promise.resolve(allowTransition);
    }

    /**
     * alert
     *
     * @param options 配置
     * @param options.msg 提示
     * @param options.title 标题
     * @param options.buttonName 按钮名称
     */
    @sdkLog(SdkType.Browser)
    alert(options: { msg?: string; title?: string; buttonName?: string }): Promise<any> {
        window.alert(options.msg);
        return Promise.resolve();
    }

    /**
     * 设置导航栏标题
     *
     * @param title 标题
     */
    @sdkLog(SdkType.Browser)
    setPageTitle(title: string): Promise<any> {
        document.title = title;
        return Promise.resolve();
    }

    /**
     * 打开新页面
     *
     * @param url 链接
     */
    @sdkLog(SdkType.Browser)
    openLink(url: string): Promise<any> {
        openInNewTab(url);
        return Promise.resolve();
    }

    /**
     * 下载图片
     *
     * @param url 图片链接
     */
    @sdkLog(SdkType.Browser)
    downloadLink({
        url
    }: {
        url: string;
        onProgress?: (options: { total: number; current: number }) => void;
        share?: boolean;
    }): Promise<any> {
        window.location.href = url;
        return Promise.resolve();
    }

    @sdkLog(SdkType.Browser)
    cancelDownloadLink(_options: { url: string }): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 下载打印文件
     *
     * @param _downloadUrl 文件下载链接
     * @param _fileType 文件类型
     */
    @sdkLog(SdkType.Browser)
    downloadPrintFile({
        url
    }: {
        url: string;
        fileType?: string;
        fileName?: string;
    }): Promise<any> {
        return this.downloadLink({ url });
    }

    /**
     * 跳转指定页面
     * @param url 跳转链接
     */
    @sdkLog(SdkType.Browser)
    navigateTo(url: string): Promise<any> {
        redirect(url);
        return Promise.resolve();
    }

    /**
     * 旋转屏幕(仅钉钉支持)
     */
    @sdkLog(SdkType.Browser)
    screenRotate(): Promise<unknown> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 重置旋转屏幕（仅钉钉支持，配合旋转屏幕接口使用）
     */
    @sdkLog(SdkType.Browser)
    resetScreenRotate(): Promise<unknown> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 钉钉全屏自动横屏，取消全屏自动竖屏
     * @param _fullscreen 是否全屏
     */
    @sdkLog(SdkType.Browser)
    screenRotateBeforeFullscreen(_fullscreen: boolean): Promise<any> {
        return Promise.resolve();
    }

    /**
     * 关闭浏览器窗口
     */
    @sdkLog(SdkType.Browser)
    closeWindow(): Promise<void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 唤起地图
     * @param _params 地图参数
     * @param _params.lng 经度
     * @param _params.lat 纬度
     * @param _params.address 地址
     */
    @defaultImpl
    @sdkLog(SdkType.Browser)
    openMap(_params: { lng: number; lat: number; address: string }): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 禁用iOS webview弹性效果
     */
    @sdkLog(SdkType.Browser)
    disableBounce(): Promise<void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 飞书应用授权
     */
    @sdkLog(SdkType.Browser)
    applyAppScope(): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    // ======= jodoo app 特殊逻辑 API =======

    /**
     * 切换企业
     * 需要提供降级逻辑
     *
     * @param _tenantId 企业ID
     * @returns {Promise}
     */
    switchCorp(_tenantId?: string): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 请求相机权限
     * 需要提供降级逻辑
     *
     * @param _type 权限类型
     * @returns {Promise}
     */
    requestCameraPermission(): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 退出登录
     * 需要提供降级逻辑
     *
     * @returns {Promise}
     */
    signout(): Promise<any> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 获取自定义请求头
     *
     * @returns {Record<string, string>}
     */
    getRequestHeaders(): Record<string, string> {
        return {};
    }

    /**
     * 获取注销账号token
     *
     * @returns {string}
     */
    deletedAccountToken(): string {
        return '';
    }

    /**
     * 相机权限变化回调
     *
     * @param _callback 回调
     * @returns {() => void}
     */
    onCameraPermissionChanged(_callback: (res: string) => void): () => void {
        return EMPTY_FN;
    }

    /** 设置当前网页的导航栏业务按钮；其他宿主默认不支持。 */
    @sdkLog(SdkType.Browser)
    setHeaderActions(_params: SetHeaderActionsParams): Promise<void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /** 清除指定侧的导航栏业务按钮。 */
    @sdkLog(SdkType.Browser)
    clearHeaderActions(_params?: ClearHeaderActionsParams): Promise<void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /** 订阅返回同步空取消函数，保证不支持的宿主也可安全清理。 */
    @sdkLog(SdkType.Browser)
    onHeaderActionClick(_callback: (event: HeaderActionClickEvent) => void): () => void {
        return EMPTY_FN;
    }

    /** 请求添加入口；其他宿主不自动降级为网页安装。 */
    @sdkLog(SdkType.Browser)
    addToDesktop(_params: AddToDesktopParams): Promise<AddToDesktopResult> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 检查当前环境是否支持该 API
     *
     * @param _functionName API 名称
     * @returns {Promise}
     */
    hasFunction(_functionName: SDKFunction): Promise<boolean> {
        return Promise.resolve(false);
    }

    /**
     * 跳转关于应用页面
     * app 环境专用，其他环境不支持
     * @returns {Promise}
     */
    goToAboutApp(): Promise<void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }

    /**
     * 跳转创建企业页面
     * app 环境专用，其他环境不支持
     * @returns {Promise}
     */
    goToCreateCorp(): Promise<void> {
        return Promise.reject(new SdkError(this.sdkType, SdkResponseCode.Unimplemented));
    }
}
